import { useEffect, useRef, useState } from "react";
import { useGameClubContext } from "../Hook/GameClubContext";
import {
  GameClubChatContent,
  GameClubChatInput,
  GameClubChatMember,
  GameClubChatMessageWrapper,
  GameClubChatWrapper,
} from "../Style/GameClubChatStyles";
import { useGameContext } from "../Hook/GameContext";
import io, { Socket } from "socket.io-client";
import { LOCAL_STORAGE_ACCESS_KEY } from "../../../../utils/network";
import { isApp } from "../../../../hooks/useNativeApp";
import { isIOSDevice } from "../../../../utils/common";

interface GameClubChatResponse {
  chatId: number;
  nickname: string;
  userId: number;
  profileImage: string | null;
  message: string;
  addedAt: string;
}

export const GameClubChat = () => {
  const { myClub } = useGameClubContext();
  const { user, showGNB, setShowGNB } = useGameContext();
  const [message, setMessage] = useState<string>("");
  const [selectClubId, setSelectClubId] = useState<number | null>(
    myClub?.clubId ?? null
  );
  const [list, setList] = useState<
    {
      date: string;
      items: GameClubChatResponse[];
    }[]
  >([]);
  const [isIOS, setIsIOS] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const isSendingRef = useRef(false);
  const messagesPerPage = 40;

  useEffect(() => {
    setIsIOS(isIOSDevice());
  }, []);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (chatWrapperRef.current) {
        chatWrapperRef.current.scrollTop = chatWrapperRef.current.scrollHeight;
      }
    });
  };

  const connectSocket = () => {
    if (socketRef.current?.connected) {
      return;
    }
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    socketRef.current = io(process.env.REACT_APP_REST_API_HOST, {
      transports: ["websocket"],
      path: "/socket.io",
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";
    socketRef.current.emit("ping:clubChatLogin", accessToken, (data: any) => {
      if (data.result === 1) {
        socketRef.current?.emit("set-token", data.login_token, (res: any) => {
          if (res.success === 1) {
            if (list.length === 0) {
              loadInitialMessages();
            }
          } else {
            console.error("로그인 실패");
          }
        });
      } else {
        console.error("로그인 실패");
      }
    });

    socketRef.current.on("from:ping:newChat", (data: GameClubChatResponse) => {
      addChatMessage(data);
    });
  };

  useEffect(() => {
    connectSocket();

    const checkConnectionInterval = setInterval(() => {
      if (socketRef.current && !socketRef.current.connected) {
        connectSocket();
      }
    }, 1000);

    return () => {
      clearInterval(checkConnectionInterval);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const chatWrapper = chatWrapperRef.current;
    if (!chatWrapper) return;

    const handleScroll = () => {
      if (
        chatWrapper.scrollTop <= 100 &&
        !isLoading &&
        hasMoreMessages &&
        socketRef.current
      ) {
        loadPreviousMessages();
      }
    };

    chatWrapper.addEventListener("scroll", handleScroll);
    return () => chatWrapper.removeEventListener("scroll", handleScroll);
  }, [isLoading, hasMoreMessages]);

  useEffect(() => {
    const chatWrapper = chatWrapperRef.current;
    const input = inputRef.current;
    if (!chatWrapper || !input) return;

    let previousKeyboardHeight = 0;

    const handleViewportResize = () => {
      if (!isApp || !window.visualViewport) return;

      const viewportHeight = window.visualViewport.height;
      const windowHeight = window.innerHeight;
      const keyboardHeight = windowHeight - viewportHeight;

      // 키보드 감지 (100px 이상 차이)
      const isKeyboard = keyboardHeight > 100;
      setIsKeyboardVisible(isKeyboard);

      if (isKeyboard) {
        // 키보드 나타날 때 채팅 영역 높이 조정
        chatWrapper.style.height = `${viewportHeight - 66}px`; // info(20px) + padding(20px) + input(36px) + margins(10px)
        input.scrollIntoView({ behavior: "smooth", block: "end" });
      } else {
        // 키보드 사라질 때 높이 초기화
        chatWrapper.style.height = "100%";
      }

      previousKeyboardHeight = keyboardHeight;
      scrollToBottom();
    };

    const handleFocus = () => {
      if (!isApp) return;
      setShowGNB(false);
      setTimeout(() => {
        handleViewportResize();
      }, 300); // 키보드 애니메이션 대기
    };

    const handleBlur = () => {
      if (isSendingRef.current || !isApp) return;

      setTimeout(() => {
        if (inputRef.current !== document.activeElement) {
          setShowGNB(true);
          setIsKeyboardVisible(false);
          chatWrapper.style.height = "100%";
          scrollToBottom();
        }
      }, 100);
    };

    // iOS 대체 이벤트
    const handleWindowResize = () => {
      if (!isApp || window.visualViewport) return;
      handleViewportResize();
    };

    window.visualViewport?.addEventListener("resize", handleViewportResize);
    window.addEventListener("resize", handleWindowResize);
    input.addEventListener("focus", handleFocus);
    input.addEventListener("blur", handleBlur);

    handleViewportResize();

    return () => {
      window.visualViewport?.removeEventListener(
        "resize",
        handleViewportResize
      );
      window.removeEventListener("resize", handleWindowResize);
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur);
      chatWrapper.style.height = "100%";
    };
  }, [setShowGNB, setIsKeyboardVisible]);

  const loadInitialMessages = () => {
    if (!socketRef.current) return;

    setIsLoading(true);
    socketRef.current.emit(
      "ping:chats",
      selectClubId,
      0,
      messagesPerPage,
      (data: any) => {
        setIsLoading(false);
        if (data && data.list && data.list.length > 0) {
          setHasMoreMessages(data.total > messagesPerPage);
          const messages = data.list.reverse();
          const groupedByDate: { [key: string]: GameClubChatResponse[] } = {};

          messages.forEach((item: GameClubChatResponse) => {
            const date = item.addedAt.split("T")[0];
            if (!groupedByDate[date]) {
              groupedByDate[date] = [];
            }
            groupedByDate[date].push(item);
          });

          const formatList = Object.keys(groupedByDate).map((date) => ({
            date,
            items: groupedByDate[date],
          }));

          setList(formatList.sort((a, b) => (a.date > b.date ? 1 : -1)));
          scrollToBottom();
        } else {
          setHasMoreMessages(false);
        }
      }
    );
  };

  const loadPreviousMessages = () => {
    if (!socketRef.current || isLoading || !hasMoreMessages) return;

    setIsLoading(true);
    const newPage = currentPage + 1;
    const previousScrollHeight = chatWrapperRef.current?.scrollHeight || 0;

    socketRef.current.emit(
      "ping:chats",
      selectClubId,
      newPage,
      messagesPerPage,
      (data: any) => {
        setIsLoading(false);
        if (data && data.list && data.list.length > 0) {
          setHasMoreMessages(newPage * messagesPerPage < data.total);
          const messages = data.list.reverse();
          const groupedByDate: { [key: string]: GameClubChatResponse[] } = {};

          messages.forEach((item: GameClubChatResponse) => {
            const date = item.addedAt.split("T")[0];
            if (!groupedByDate[date]) {
              groupedByDate[date] = [];
            }
            groupedByDate[date].push(item);
          });

          const newList = [...list];
          Object.keys(groupedByDate).forEach((date) => {
            let dateGroup = newList.find((item) => item.date === date);
            if (!dateGroup) {
              dateGroup = { date, items: [] };
              newList.push(dateGroup);
            }
            dateGroup.items.unshift(...groupedByDate[date]);
          });

          setList(newList.sort((a, b) => (a.date > b.date ? 1 : -1)));
          setCurrentPage(newPage);

          requestAnimationFrame(() => {
            if (chatWrapperRef.current) {
              const newScrollHeight = chatWrapperRef.current.scrollHeight;
              chatWrapperRef.current.scrollTop =
                newScrollHeight - previousScrollHeight;
            }
          });
        } else {
          setHasMoreMessages(false);
        }
      }
    );
  };

  const addChatMessage = (data: GameClubChatResponse) => {
    const date = data.addedAt.split("T")[0];
    setList((prevList) => {
      const newList = [...prevList];
      const dateGroupIndex = newList.findIndex((item) => item.date === date);

      if (dateGroupIndex === -1) {
        newList.push({ date, items: [data] });
      } else {
        newList[dateGroupIndex] = {
          ...newList[dateGroupIndex],
          items: [...newList[dateGroupIndex].items, data],
        };
      }

      return newList.sort((a, b) => (a.date > b.date ? 1 : -1));
    });

    scrollToBottom();
  };

  const handleSend = () => {
    if (!message.trim() || !socketRef.current || !user) return;

    isSendingRef.current = true;
    socketRef.current.emit(
      "ping:sendChat",
      selectClubId,
      message,
      (data: any) => {
        setMessage("");
        isSendingRef.current = false;
      }
    );
  };

  const formatDate = (value: string) => {
    const [yyyy, mm, dd] = value.split("-");
    return `${yyyy}년 ${mm}월 ${dd}일`;
  };

  const formatTime = (value: string) => {
    const currentTime = new Date();
    const targetTime = new Date(value);
    const timeDifference = currentTime.getTime() - targetTime.getTime();

    const dayDifference = Math.floor(timeDifference / (24 * 1000 * 60 * 60));
    const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
    const minutesDifference = Math.floor(timeDifference / (1000 * 60));

    if (dayDifference >= 1) {
      return `${dayDifference}일 전`;
    } else if (hoursDifference >= 1) {
      return `${hoursDifference}시간 전`;
    } else if (minutesDifference >= 1) {
      return `${minutesDifference}분 전`;
    } else {
      return "방금 전";
    }
  };

  return (
    <GameClubChatWrapper showGNB={showGNB}>
      <div className="info">
        클럽원들과 자유롭게 이야기 할 수 있는 클럽 채팅입니다. <br />
        타인에게 불쾌감을 주는 모든 언행은 운영수칙에 따라 제재될 수 있습니다.
      </div>
      <GameClubChatContent showGNB={showGNB} isIos={isIOS}>
        <div className="chat-wrapper" ref={chatWrapperRef}>
          {list.map((date, dIndex) => (
            <div key={dIndex} className="date-wrapper">
              <div className="date">{formatDate(date.date)}</div>
              {date.items.map((item, index) => (
                <div
                  className={`${item.userId === user?.id ? "mine" : "member"}`}
                  key={`chat-${item.chatId}-${index}`}
                >
                  {item.userId !== user?.id ? (
                    <>
                      {item.profileImage ? (
                        <img src={item.profileImage} alt="profile" />
                      ) : (
                        <img
                          src="/image-web/game/club/club_profile.png"
                          alt="profile"
                        />
                      )}
                      <GameClubChatMember>
                        <div className="nickname">{item.nickname}</div>
                        <GameClubChatMessageWrapper isMain={false}>
                          <div className="message">{item.message}</div>
                          <div className="time">{formatTime(item.addedAt)}</div>
                        </GameClubChatMessageWrapper>
                      </GameClubChatMember>
                    </>
                  ) : (
                    <GameClubChatMessageWrapper isMain={true}>
                      <div className="time">{formatTime(item.addedAt)}</div>
                      <div className="message">{item.message}</div>
                    </GameClubChatMessageWrapper>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <GameClubChatInput>
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === "Enter") handleSend();
            }}
            placeholder="대화내용을 입력하세요."
          />
          <img
            className="send"
            src="/image-web/game/club/send.svg"
            alt="전송하기"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSend();
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSend();
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
          />
        </GameClubChatInput>
      </GameClubChatContent>
    </GameClubChatWrapper>
  );
};
